
import Vue from "vue"
import { mapGetters, mapActions } from "vuex"
import { ValidationProvider } from "vee-validate"
import { extend } from "vee-validate"
import { required } from "vee-validate/dist/rules"
import { getAttributes } from "~/utils/block"
import {
  SET_DELIVERY_FLOOR,
  SET_DELIVERY_PROPERTY_TYPE,
  SET_LISTING,
  SET_DELIVERY_LIFT_AVAILABLE
} from "~/store/listing/actions"
import {
  GET_DELIVERY_FLOOR,
  GET_DELIVERY_LIFT_AVAILABLE,
  GET_DELIVERY_PROPERTY_TYPE
} from "~/store/listing/getters"
import InitialListing from "~/store/listing/state"
import { isFlat, isHouse } from "~/services/public/Listing"
import {
  FLOOR_GROUND,
  FLOORS,
  LIFT_AVAILABLE,
  LIFT_UNAVAILABLE,
  PROPERTY_TYPE_LIST_FLAT,
  PROPERTY_TYPE_LIST_HOUSE,
  PROPERTY_TYPE_LIST_MAIN
} from "~/constants/listing"
import {
  GET_FLOORS,
  GET_PROPERTY_TYPES,
  GET_PROPERTY_TYPES_MAIN,
  GET_PROPERTY_TYPES_FLAT,
  GET_PROPERTY_TYPES_HOUSE
} from "~/store/property/getters"
import { swapFirstTwoElements } from "~/scripts/useful-functions"

interface ComponentData {
  propertyTypesMain: any[]
  propertyTypesHouse: any[]
  propertyTypesFlat: any[]
  floors: any[]
}

interface LiftOption {
  name: string
  value: number
}

export default Vue.extend({
  name: "PropertyDeliveryInput",

  components: {
    ValidationProvider
  },

  props: {
    propertyTypeHidden: {
      type: Boolean
    },
    propertyFloorHidden: {
      type: Boolean
    },
    block: {
      type: Object,
      required: true
    },
    virgin: {
      type: Boolean
    },
    lazy: {
      type: Boolean,
      default: true
    },
    validation: {
      type: Boolean,
      default: true
    }
  },

  data(): ComponentData {
    return {
      propertyTypesMain: PROPERTY_TYPE_LIST_MAIN,
      propertyTypesHouse: PROPERTY_TYPE_LIST_HOUSE,
      propertyTypesFlat: PROPERTY_TYPE_LIST_FLAT,
      floors: FLOORS
    }
  },

  computed: {
    ...mapGetters("listing", {
      getDeliveryFloor: GET_DELIVERY_FLOOR,
      getDeliveryLiftAvailable: GET_DELIVERY_LIFT_AVAILABLE,
      getDeliveryPropertyType: GET_DELIVERY_PROPERTY_TYPE
    }),
    ...mapGetters("property", {
      getStorePropertyTypes: GET_PROPERTY_TYPES,
      getStorePropertyTypesMain: GET_PROPERTY_TYPES_MAIN,
      getStorePropertyTypesHouse: GET_PROPERTY_TYPES_HOUSE,
      getStorePropertyTypesFlat: GET_PROPERTY_TYPES_FLAT,
      getStoreFloors: GET_FLOORS
    }),

    getPropertyTypesMain(): any[] {
      if (!this.getStorePropertyTypesMain) return this.propertyTypesMain

      const propertyTypesToShow = this.getStorePropertyTypesMain.filter(type => !type.hidden)

      if (this.$i18n.locale === "en") {
        return propertyTypesToShow
      }
      if (this.$i18n.locale === "es") {
        return swapFirstTwoElements(propertyTypesToShow)
      }
      return propertyTypesToShow
    },

    getPropertyTypesHouse(): any[] {
      return this.getStorePropertyTypesHouse || this.propertyTypesHouse
    },

    getPropertyTypesFlat(): any[] {
      return this.getStorePropertyTypesFlat || this.propertyTypesFlat
    },

    getFloors(): any[] {
      return this.getStoreFloors || this.floors
    },

    attributes(): Record<string, any> | null {
      return this.virgin ? null : this.getAttributes(this.block)
    },

    deliveryPropertyType: {
      get(): string | null {
        return this.getDeliveryPropertyType
      },
      set(value: string): void {
        if (!this.isFlat(value) || this.getDeliveryFloor === FLOOR_GROUND) {
          this.setDeliveryFloor(FLOOR_GROUND)
          this.setLiftAvailable(null)
        }
        this.setDeliveryPropertyType(value)
      }
    },

    deliveryFloor: {
      get(): string | null {
        return this.getDeliveryFloor
      },
      set(value: string | null): void {
        if (value === FLOOR_GROUND) {
          this.setLiftAvailable(null)
        }
        this.setDeliveryFloor(value)
      }
    },

    liftAvailable: {
      get(): number | null {
        if (this.getDeliveryLiftAvailable === null) {
          this.setLiftAvailable(LIFT_AVAILABLE)
          return LIFT_AVAILABLE
        }
        return this.getDeliveryLiftAvailable
      },
      set(value: number | null): void {
        this.setLiftAvailable(value)
      }
    },

    getLiftAvailableOptions(): LiftOption[] {
      return [
        {
          name: this.$t("location_form.lift_available") as string,
          value: LIFT_AVAILABLE
        },
        {
          name: this.$t("location_form.lift_not_available") as string,
          value: LIFT_UNAVAILABLE
        }
      ]
    }
  },

  created() {
    if (this.validation) {
      extend("required", required)
    }
    this.setListing(InitialListing())
  },

  methods: {
    ...mapActions("listing", {
      setDeliveryPropertyType: SET_DELIVERY_PROPERTY_TYPE,
      setDeliveryFloor: SET_DELIVERY_FLOOR,
      setLiftAvailable: SET_DELIVERY_LIFT_AVAILABLE,
      setListing: SET_LISTING
    }),

    getAttributes,

    isHouse(propertyType: string): boolean {
      return isHouse(propertyType) as boolean
    },

    isFlat(propertyType: string): boolean {
      return isFlat(propertyType) as boolean
    },

    requiresFloor(propertyType: string): boolean {
      if (!this.getStorePropertyTypes) {
        return this.propertyTypeHidden && !this.propertyFloorHidden
      }
      const propertyTypeObject = this.getStorePropertyTypes.find(
        storePropertyType => storePropertyType.value === propertyType
      )
      return !!propertyTypeObject?.propertyWithLevels
    }
  }
})
